import { AuthPaths, useAuth } from "@wojo/auth";
import { useI18n } from "@wojo/localization";
import { Form } from "@wojo/ui";
import React, { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";
import { useTripBuilderPublishDraftQuoteMutation } from "../../../../generated/graphql";
import { useHandleCustomerChange } from "./use-handle-customer-change";
import { useDraftQuoteFragment } from "./use-draft-quote-fragment";

export type FormValues = {
    nbTravelers: number;
    flightOption: "noFlights" | "yesFlights";
    mostRecentGateway?: string;
};

export const TripBuilderQuoteForm: React.FC<
    PropsWithChildren<{
        draftId?: string;
        setError: React.Dispatch<React.SetStateAction<string>>;
        navigationLoadingTimeout: () => void;
    }>
> = ({ children, setError, draftId, navigationLoadingTimeout }) => {
    const { isLoggedIn, setView, webSource } = useAuth();
    const { global } = useI18n("global");
    const [publishDraftQuote] = useTripBuilderPublishDraftQuoteMutation();
    const handleCustomerChange = useHandleCustomerChange(draftId, setError);
    const draftQuote = useDraftQuoteFragment(draftId);
    const defaultHomeGatewayCode =
        draftQuote?.order.trips.at(0)?.openDepartingFlightLineItem
            ?.departureGatewayCode;

    const isDefaultLandOnly = defaultHomeGatewayCode === "LO1";

    const form = useForm<FormValues>({
        defaultValues: {
            nbTravelers: 1,
            flightOption: isDefaultLandOnly ? "noFlights" : "yesFlights",
            mostRecentGateway: isDefaultLandOnly
                ? undefined
                : defaultHomeGatewayCode,
        },
        mode: "all",
    });

    const continueUrl = `${
        (webSource === "GROUPS" && process.env.NEXT_PUBLIC_WEB_DOMAIN) || ""
    }/checkout/${draftQuote?._id}/customize`;

    const handleSubmit = async () => {
        setError("");
        if (!!draftQuote?._id) {
            if (!isLoggedIn) {
                setView?.({
                    view: AuthPaths.VerifyEmailCheckout,
                    // handle password login
                    onLoginComplete: (customerId) =>
                        handleCustomerChange(customerId, () => {
                            navigationLoadingTimeout();
                            window.location.href = continueUrl;
                        }),
                    // handle magic link login
                    onLoginSuccessPath: continueUrl,
                    hideCTAs: true,
                });
            } else {
                // handle logged in user
                await publishDraftQuote({
                    variables: {
                        id: draftQuote._id,
                    },
                    onCompleted: () => {
                        navigationLoadingTimeout();
                        window.location.href = continueUrl;
                    },
                    onError: () => {
                        setError(global.errors.generic.l());
                    },
                });
            }
        } else {
            setError(global.errors.generic.l());
        }
    };

    return (
        <Form<FormValues>
            form={form}
            onSubmit={async () => {
                await handleSubmit();
            }}
        >
            {children}
        </Form>
    );
};
